import React, {useMemo} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {isBefore} from 'date-fns/isBefore'

import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {getDayPriceInfo} from '@vio/core/src/_web/availability'
import {AvailabilityHotelEntity} from '@vio/core/src/availability/types'
import {
  dateFormat,
  dateToMiddayDate,
  UTS_DATE_FORMAT
} from '@vio/core/src/utils/date'
import {getRoundingStrategy} from '@vio/core/src/utils/number'

import {Price} from '../../Price'

export const DAY_SIZE = 44

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  outline: none;
  flex-wrap: wrap;
  height: ${DAY_SIZE}px;
  &.--should-show-price {
    align-items: flex-start;
    padding-top: ${cssTheme.layout.spacing.s200};
  }
  &.low {
    background-color: ${cssTheme.colors.background.success.tertiary};
    border-radius: ${cssTheme.layout.radius.lg};
    .selected:not(.start):not(.startEnd) & {
      background-color: ${cssTheme.colors.background.interactive
        .secondaryActiveHover};
    }
    .startEnd & {
      background-color: ${cssTheme.colors.background.interactive.primary};
    }
  }

  &.high {
    background-color: ${cssTheme.colors.background.danger.tertiary};
    border-radius: ${cssTheme.layout.radius.lg};
  }
  .end & {
    .price,
    .loading {
      display: none;
    }
  }
`

const DayContainer = styled(Text)`
  width: 100%;
`

const PriceWrapper = styled(Text)`
  display: flex;
  justify-content: center;
  width: 100%;
  color: ${cssTheme.colors.content.neutral.secondary};
  .selected:not(.start) & {
    color: ${cssTheme.colors.content.interactive.primary};
  }
  .low & {
    color: ${cssTheme.colors.content.success.secondary};
  }
  .high & {
    color: ${cssTheme.colors.content.danger.secondary};
  }
  .startEnd & {
    color: ${cssTheme.colors.content.neutral.inverse};
  }
`

interface Props {
  day: Date
  hotelAvailabilityPrices?: AvailabilityHotelEntity
  searchComplete?: boolean
  showTotalPrices?: boolean
  noHighPrices?: boolean
  noUnavailabilityIcon?: boolean
  shouldShowPrices: boolean
  /** Decimals kept on the abbreviated price. Set for high-magnitude currencies
   * (e.g. CLP) where the default single decimal collapses distinct prices. */
  maximumFractionDigits?: number
  /* Range for price type calculations, defaults to current month */
  comparisonDateRange?: {start: Date; end: Date}
}

export const ColorPriceDayInner: React.FC<Props> = ({
  day,
  hotelAvailabilityPrices,
  searchComplete,
  showTotalPrices,
  noHighPrices = false,
  noUnavailabilityIcon = false,
  shouldShowPrices,
  maximumFractionDigits,
  comparisonDateRange
}) => {
  const stringDate = dateFormat(day, UTS_DATE_FORMAT)
  const {price, currency, priceType, displayPrice} = useMemo(
    () =>
      getDayPriceInfo(hotelAvailabilityPrices, stringDate, {
        showTotalPrices,
        noHighPrices,
        comparisonDateRange
      }),
    [
      hotelAvailabilityPrices,
      showTotalPrices,
      stringDate,
      noHighPrices,
      comparisonDateRange
    ]
  )

  const isDateBeforeToday = isBefore(
    dateToMiddayDate(day),
    dateToMiddayDate(new Date())
  )

  const shouldShowUnavailability =
    !noUnavailabilityIcon && !price && searchComplete && !isDateBeforeToday

  const shouldShowLoader = !price && !searchComplete && shouldShowPrices

  return (
    <Wrapper
      className={cx(
        shouldShowPrices && '--should-show-price',
        `day-inner ${priceType}`
      )}
      data-id={displayPrice ? 'DayWithPrice' : undefined}
    >
      <DayContainer className="label" variant="labelS" tag={'span'}>
        {day.getDate()}
      </DayContainer>
      {shouldShowPrices && displayPrice && (
        <PriceWrapper variant="bodyXS" className="price" tag={'span'}>
          <Price
            noStyle
            currency={currency}
            value={displayPrice}
            roundingStrategy={getRoundingStrategy()}
            isAbbreviated
            useShortCurrency
            maximumFractionDigits={maximumFractionDigits}
          />
        </PriceWrapper>
      )}
      {shouldShowUnavailability && (
        <div>
          <Icon size="sm" name="Forbid" colorPath="content.neutral.disabled" />
        </div>
      )}
      {shouldShowLoader && (
        <div className="loading">
          <Text variant="bodyXS" colorPath="content.neutral.disabled">
            -
          </Text>
        </div>
      )}
    </Wrapper>
  )
}
